import { useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { loadGoogleMapsScript } from "@/utils/loadGoogleMapsScript";

export type AddressMapPreviewProps = {
  lat: number;
  lng: number;
  /** Shown as a small caption above the map; also used for the map region aria-label. */
  caption: string;
  /** When true, users cannot pan/zoom the preview (e.g. form disabled). */
  disableInteractions?: boolean;
  className?: string;
  /**
   * Same value as `VITE_GOOGLE_MAPS_API_KEY` where the map is shown.
   * Required so the first open awaits the shared Maps script before init; otherwise the effect can exit once and never retry until lat/lng change.
   */
  mapsApiKey?: string;
};

type GoogleMapsNamespace = typeof google.maps;

function getMapsNamespace(): GoogleMapsNamespace | null {
  const w = globalThis as typeof globalThis & { google?: { maps: GoogleMapsNamespace } };
  return w.google?.maps ?? null;
}

/**
 * Embedded Google Map centered on a point with a marker. Requires Maps JS API already loaded.
 * Triggers "resize" when the container size changes (e.g. dialog open) so the map is not blank.
 *
 * Uses `globalThis.google` because bare `google` is not a binding in ES modules and can throw ReferenceError.
 */
export function AddressMapPreview({
  lat,
  lng,
  caption,
  disableInteractions = false,
  className,
  mapsApiKey,
}: AddressMapPreviewProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const positionRef = useRef({ lat, lng });
  positionRef.current = { lat, lng };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let cancelled = false;

    void (async () => {
      if (!el || cancelled) return;

      const key = mapsApiKey?.trim();
      if (key) {
        try {
          await loadGoogleMapsScript(key);
        } catch {
          return;
        }
      }

      if (!el || cancelled) return;

      let mapsNs = getMapsNamespace();
      if (!mapsNs && !key) {
        for (let i = 0; i < 40 && !cancelled && !mapsNs; i++) {
          await new Promise<void>((resolve) => setTimeout(resolve, 50));
          mapsNs = getMapsNamespace();
        }
      }
      if (!mapsNs || !el || cancelled) return;

      const syncMapToContainer = (map: google.maps.Map): void => {
        const pos = positionRef.current;
        mapsNs.event.trigger(map, "resize");
        map.setCenter(pos);
        markerRef.current?.setPosition(pos);
      };

      const scheduleSync = (map: google.maps.Map): void => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!cancelled && mapRef.current === map) {
              syncMapToContainer(map);
            }
          });
        });
      };

      const { Map } = (await mapsNs.importLibrary("maps")) as google.maps.MapsLibrary;
      if (cancelled || !el) return;
      const position = { lat, lng };
      const gestureHandling = disableInteractions ? ("none" as const) : ("auto" as const);

      if (!mapRef.current) {
        const map = new Map(el, {
          center: position,
          zoom: 17,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling,
        });
        mapRef.current = map;
        markerRef.current = new mapsNs.Marker({ position, map });

        if (!resizeObserverRef.current) {
          resizeObserverRef.current = new ResizeObserver(() => {
            if (!mapRef.current) return;
            syncMapToContainer(mapRef.current);
          });
          resizeObserverRef.current.observe(el);
        }

        scheduleSync(map);
      } else {
        mapRef.current.setCenter(position);
        mapRef.current.setOptions({ gestureHandling });
        markerRef.current?.setPosition(position);
        scheduleSync(mapRef.current);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lat, lng, disableInteractions, mapsApiKey]);

  useEffect(() => {
    /*
      CAPTURED AT EFFECT TIME, not read in the cleanup. React may have detached
      the node by the time cleanup runs, so containerRef.current can already be
      null — and then the map's DOM is never emptied. The node this effect
      actually drew into is the one that has to be cleared.
    */
    const drawnInto = containerRef.current;
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      markerRef.current?.setMap(null);
      markerRef.current = null;
      mapRef.current = null;
      if (drawnInto) drawnInto.innerHTML = "";
    };
  }, []);

  return (
    <div className={cn("space-y-1.5", className)}>
      <Label>{caption}</Label>
      <div
        className="relative h-[200px] w-full overflow-hidden rounded-md border border-border motion-reduce:transition-none"
        role="region"
        aria-label={caption}
      >
        <div ref={containerRef} className="absolute inset-0 h-full w-full" />
      </div>
    </div>
  );
}
